"use client"

import { createContext, useContext, useState, useEffect, useCallback } from "react"
// import { checkSubscriptionStatus } from "../../utils/checkSubscription"
import { TOOL_CODE } from "../../config"
import { useAuth } from "./AuthContext"
import { checkSubscriptionStatus } from "../../services/checkSubscription"

const TokenContext = createContext()

export const useToken = () => {
  const context = useContext(TokenContext)
  if (!context) {
    throw new Error("useToken must be used within a TokenProvider")
  }
  return context
}

export const TokenProvider = ({ children }) => {
  const { user, isAuthenticated, userData, isPaidUser, getUserPlanType } = useAuth()
  const [tokenData, setTokenData] = useState({
    canAfford: null,
    availableTokens: 0,
    requiredTokens: 0,
    platform: null,
    featureId: null,
    loading: true,
    error: null,
    lastChecked: null,
    alert: null,
  })

  const checkTokenAvailability = useCallback(
    async (forceRefresh = false, featureId = null) => {
      // Don't check if user is not authenticated
      if (!isAuthenticated || !user) {
        setTokenData((prev) => ({ ...prev, loading: false, canAfford: false }))
        return { success: false, message: "User not authenticated" }
      }

      const planType = getUserPlanType()

      // Use cached data if available and not forcing refresh (cache for 5 minutes)
      const now = Date.now()
      const cacheExpiry = 5 * 60 * 1000 // 5 minutes
      const lastChecked = tokenData.lastChecked
      if (!forceRefresh && lastChecked && now - lastChecked < cacheExpiry) {
        return {
          success: tokenData.canAfford,
          ...tokenData,
          cached: true,
          planType,
          showUpgradePrompt: planType === "free_plan",
        }
      }

      setTokenData((prev) => ({ ...prev, loading: true, error: null }))

      try {
        const result = await checkSubscriptionStatus(TOOL_CODE, featureId)

        const newTokenData = {
          canAfford: result.canAfford,
          availableTokens: result.usageRemaining || 0,
          requiredTokens: result.requiredTokens || 1,
          platform: result.platform || null,
          featureId: result.featureId || null,
          loading: false,
          error: null,
          lastChecked: now,
          message: result.message,
          alert: result.alert,
        }

        setTokenData(newTokenData)

        return {
          success: result.success,
          ...newTokenData,
          planType,
          showUpgradePrompt: planType === "free_plan",
          displayMessage:
            result.alert?.message ||
            (planType === "free_plan"
              ? "You are currently on the free plan. Upgrade to access generation features."
              : result.canAfford
                ? `You have ${result.usageRemaining} tokens available.`
                : "Insufficient tokens. Please upgrade your plan or wait for token refresh."),
        }
      } catch (error) {
        console.error("Error checking token availability:", error)
        const errorData = {
          canAfford: false,
          availableTokens: 0,
          requiredTokens: 1,
          platform: null,
          featureId: null,
          loading: false,
          error: error.message || "Failed to check token availability",
          lastChecked: now,
          alert: null,
        }

        setTokenData(errorData)

        return {
          success: false,
          ...errorData,
          planType,
          showUpgradePrompt: planType === "free_plan",
          displayMessage:
            planType === "free_plan"
              ? "You are currently on the free plan. Upgrade to access generation features."
              : "Unable to check token availability. Please try again.",
        }
      }
    },
    [isAuthenticated, user, getUserPlanType], // Added getUserPlanType dependency
  )

  useEffect(() => {
    if (isAuthenticated && user && userData) {
      checkTokenAvailability()
    }
  }, [isAuthenticated, user, userData, checkTokenAvailability])

  const refreshTokenData = useCallback(
    (featureId = null) => {
      return checkTokenAvailability(true, featureId)
    },
    [checkTokenAvailability],
  )

  const consumeTokens = useCallback((tokensUsed = 1) => {
    setTokenData((prev) => ({
      ...prev,
      availableTokens: Math.max(0, prev.availableTokens - tokensUsed),
      canAfford: prev.availableTokens - tokensUsed >= prev.requiredTokens,
      lastChecked: null, // Force refresh on next check
    }))
  }, [])

  const canGenerate = useCallback(() => {
    const planType = getUserPlanType()
    if (planType === "free_plan") return false
    return tokenData.canAfford === true
  }, [tokenData.canAfford, getUserPlanType])

  const getDisplayMessage = useCallback(() => {
    const planType = getUserPlanType()

    if (planType === "free_plan") {
      return "You are currently on the free plan. Upgrade to access generation features."
    }

    // For paid plan users, check backend alert first
    if (tokenData.alert?.message) {
      return tokenData.alert.message
    }

    // For paid plan users without backend alert
    if (tokenData.canAfford) {
      return `You have ${tokenData.availableTokens} tokens available.`
    } else {
      return "Insufficient tokens for this generation. Please upgrade your plan or wait for token refresh."
    }
  }, [tokenData.canAfford, tokenData.availableTokens, tokenData.alert, getUserPlanType])

  const getAlertTitle = useCallback(() => {
    if (tokenData.alert?.title) {
      return tokenData.alert.title
    }

    const planType = getUserPlanType()
    if (planType === "free_plan") {
      return "Upgrade Required"
    } else {
      return tokenData.canAfford ? "Tokens Available" : "Credit Limit Reached"
    }
  }, [tokenData.alert, tokenData.canAfford, getUserPlanType])

  const value = {
    ...tokenData,
    checkTokenAvailability,
    refreshTokenData,
    consumeTokens,
    // Helper methods
    hasTokens: tokenData.canAfford === true,
    isLoading: tokenData.loading,
    needsRefresh: !tokenData.lastChecked || Date.now() - tokenData.lastChecked > 5 * 60 * 1000,
    canGenerate,
    getDisplayMessage,
    getAlertTitle,
    showUpgradePrompt: getUserPlanType() === "free_plan",
    isPaidUser: isPaidUser(),
    planType: getUserPlanType(),
  }

  return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>
}
